// @flow
import * as React from "react";
import { connect, type MapStateToProps } from "react-redux";
import { rgba } from "polished";
import styled, { css } from "styled-components";
import Money from "../Money";
import { gaReducer } from "../GA";
import Gettext from "../common/Gettext";
import { rtl, fontSize } from "../common/styled-mixins";
import { TableItem } from "./Social";
import Flags from "./Flags";
import { TABS_LIMIT } from "../../redux/reducer/tabs";
import { get } from "../common/fetch";

import type { State as ReduxState } from "../../redux/reducer";
import type { State as TabsType } from "../../redux/reducer/tabs";
import type { SocialTrading } from "../../redux/commonTypes";

const Item = styled.div`
  flex-grow: 0;
  flex-shrink: 0;
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  box-sizing: border-box;
  width: 100%;
  height: 46px;
  background-color: ${({ theme }) => theme.main};
  border-bottom: 1px solid transparent;
  overflow: hidden;
  cursor: pointer;

  &:not(:last-of-type) {
    border-bottom-color: ${({ theme }) => rgba(theme.foreground, 0.2)};
  }
`;

const Stock = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  flex: 1;
  font-size: 12px;
  color: ${({ theme }) => theme.foreground};
  text-transform: uppercase;
`;

const FlagsContainer = styled.div`
  flex-grow: 0;
  flex-shrink: 0;
  position: relative;
  display: flex;
  flex-flow: row nowrap;
  width: 28px;
  height: 28px;
  ${rtl("margin-right", "6px")};
  color: ${({ theme }) => theme.mainDark};
`;

const Title = styled.span`
  ${rtl("margin-right", "6px")};
`;

const BidType = styled.div`
  flex: 0;
  width: 7px;
  height: 6px;
  border-left: 3px solid transparent;
  border-right: 3px solid transparent;

  ${({ isBidUp, theme }) =>
    isBidUp === true
      ? css`
          border-bottom: 7px solid ${theme.upper};
        `
      : css`
          border-top: 7px solid ${theme.lower};
        `};
`;

const TraderID = styled.span`
  font-size: 12px;
  color: #99959d;
`;

const MoneyContainer = styled.span`
  width: 100%;
  font-weight: 700;
  font-size: 12px;
  ${rtl("text-align", "right")};
  color: ${({ theme, isPositive }) => {
    switch (isPositive) {
      case 1:
        return theme.upper;
      case -1:
        return theme.lower;
      case 0:
      default:
        return theme.foreground;
    }
  }};
`;

const Placeholder = styled.div`
  position: relative;
  align-self: stretch;
  display: flex;
  align-items: center;
  align-content: center;
  justify-content: center;
  width: 100%;
  padding: 15px;
  padding-top: 45px;
  font-size: ${fontSize(14)};
  color: ${({ theme }) => theme.foreground};
  overflow-y: hidden;
`;

type ItemProps = {
  stock: string,
  isBidUp: boolean,
  user_id: number | string,
  profit: number
};

const SocialItem = (props: ItemProps) => (
  <React.Fragment>
    <TableItem column="assets">
      <Stock>
        <FlagsContainer>
          <Flags stock={props.stock} />
        </FlagsContainer>
        <Title>{props.stock}</Title>
      </Stock>
      <BidType isBidUp={props.isBidUp} />
    </TableItem>
    <TableItem column="trader">
      <TraderID>{props.user_id}</TraderID>
    </TableItem>
    <TableItem column="profit">
      <MoneyContainer isPositive={Math.sign(props.profit)}>
        <Money currency="USD">{props.profit}</Money>
      </MoneyContainer>
    </TableItem>
  </React.Fragment>
);

type Props = {
  tabs: TabsType,
  dispatch: Dispatch,
  social: Array<SocialTrading>,
  onItemClick?: () => void
};
const merge = data => {
  return Object.keys(data).reduce((acc, curr) => {
    return acc.concat(data[curr]);
  }, []);
};

const SocialItems = (props: Props) =>
  React.useMemo(
    () => (
      <React.Fragment>
        {props.social.length < 1 && (
          <Placeholder withParent={false}>
            <Gettext>No items to show</Gettext>
          </Placeholder>
        )}
        {props.social.map((item: SocialTrading) => (
          <Item
            key={item.id + item.timestamp}
            onClick={() => {
              get("/api/getAssets")
                .then(merge)
                .then(stocksInfo => {
                  const stock = stocksInfo.find(
                    info => item.stock.toUpperCase() === info.stock
                  );
                  const isCorrectStock = stock && stock.enabled;
                  gaReducer("CLICK_SOCIAL_TRADING_ASSET", {
                    asset: item.stock
                  });

                  if (!isCorrectStock) return false;

                  const tabIndex = props.tabs.tabs.indexOf(item.stock);
                  const tabsLength = props.tabs.tabs.length;

                  if (tabIndex !== -1) {
                    props.dispatch({
                      type: "SET_ACTIVE_TAB",
                      payload: tabIndex
                    });
                    if (props.onItemClick) props.onItemClick();
                    return;
                  }

                  if (tabsLength < TABS_LIMIT) {
                    props.dispatch({ type: "PUSH_TAB", payload: item.stock });
                    if (props.onItemClick) props.onItemClick();
                    return;
                  }

                  props.dispatch({
                    type: "SET_ACTIVE_STOCK",
                    payload: {
                      newStock: item.stock,
                      activeStock: item.stock
                    }
                  });
                  if (props.onItemClick) props.onItemClick();
                })
                .catch(error => {
                  console.log(error);
                  return false;
                });
            }}
          >
            <SocialItem
              stock={item.stock}
              isBidUp={item.isBidUp}
              user_id={item.user_id}
              profit={item.profit}
            />
          </Item>
        ))}
      </React.Fragment>
    ),
    [props.social.length > 0 ? props.social[0].timestamp : null]
  );

const mapStateToProps = (state: ReduxState) => {
  return {
    tabs: state.tabs,
    social: state.socialTrading,
    assets: state.stocks
  };
};

export default connect((mapStateToProps: MapStateToProps<*, *, *>))(
  SocialItems
);



// WEBPACK FOOTER //
// ./src/components/SocialTrading/Items.js